import { useEffect, useState } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { isFormStylingApplied, isButtonStylingApplied } from '../../utils/sectionFormStyling'

export default function ReviewForm({ website, section, sectionFormStyling, onReviewSubmit, useDefaultStyling = true, sectionType = 'reviews', customerProfile = null, customerAuthToken = null }) {
  const [form, setForm] = useState({ visitorName: '', visitorEmail: '', comment: '', rating: '' })
  const [loading, setLoading] = useState(false)
  const [isFormHovered, setIsFormHovered] = useState(false)
  const [isButtonHovered, setIsButtonHovered] = useState(false)

  useEffect(() => {
    if (!customerProfile) return
    setForm((prev) => ({
      ...prev,
      visitorName: prev.visitorName || `${customerProfile.firstName || ''} ${customerProfile.lastName || ''}`.trim(),
      visitorEmail: prev.visitorEmail || customerProfile.email || ''
    }))
  }, [customerProfile])

  // Default values to fallback to if styling is not provided
  const DEFAULT_FORM_COLORS = {
    backgroundColor: '#FFFFFF',
    textColor: '#111827',
    hoverBackgroundColor: '#F9FAFB',
    hoverTextColor: '#374151',
    buttonBackgroundColor: '#3B82F6',
    buttonTextColor: '#FFFFFF',
    hoverButtonBackgroundColor: '#2563eb',
    hoverButtonTextColor: '#ffffff'
  }

  const isFormStyling = useDefaultStyling || isFormStylingApplied(sectionFormStyling, sectionType, section?._id)
  const isButtonStyling = useDefaultStyling || isButtonStylingApplied(sectionFormStyling, sectionType, section?._id)
  
  // Ensure we have valid styling values
  const formColors = isFormStyling ? (sectionFormStyling || DEFAULT_FORM_COLORS) : undefined
  
  const formStyle = isFormStyling ? {
    backgroundColor: formColors?.backgroundColor || DEFAULT_FORM_COLORS.backgroundColor,
    color: formColors?.textColor || DEFAULT_FORM_COLORS.textColor
  } : undefined
  const hoverFormStyle = isFormStyling ? {
    backgroundColor: formColors?.hoverBackgroundColor || formColors?.backgroundColor || DEFAULT_FORM_COLORS.hoverBackgroundColor,
    color: formColors?.hoverTextColor || formColors?.textColor || DEFAULT_FORM_COLORS.hoverTextColor
  } : undefined
  const labelStyle = isFormStyling ? { color: formColors?.textColor || DEFAULT_FORM_COLORS.textColor } : undefined
  const inputStyle = isFormStyling ? { 
    backgroundColor: formColors?.backgroundColor || DEFAULT_FORM_COLORS.backgroundColor,
    color: formColors?.textColor || DEFAULT_FORM_COLORS.textColor,
    borderColor: formColors?.textColor || DEFAULT_FORM_COLORS.textColor
  } : undefined
  const buttonStyle = isButtonStyling ? {
    backgroundColor: formColors?.buttonBackgroundColor || DEFAULT_FORM_COLORS.buttonBackgroundColor,
    color: formColors?.buttonTextColor || DEFAULT_FORM_COLORS.buttonTextColor
  } : { backgroundColor: '#3B82F6', color: '#FFFFFF' } // Always provide default button styling
  const hoverButtonStyle = isButtonStyling ? {
    backgroundColor: formColors?.hoverButtonBackgroundColor || formColors?.buttonBackgroundColor || DEFAULT_FORM_COLORS.hoverButtonBackgroundColor,
    color: formColors?.hoverButtonTextColor || formColors?.buttonTextColor || DEFAULT_FORM_COLORS.hoverButtonTextColor
  } : { backgroundColor: '#2563EB', color: '#FFFFFF' }

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const rawComment = form.comment || form.message || form.reviewMessage || form.text || ''
    const trimmedComment = rawComment.toString().trim()

    if (!form.visitorName.trim()) return toast.error('Name is required')
    if (!trimmedComment) return toast.error('Comment is required')

    setLoading(true)
    try {
      const payload = {
        websiteId: website._id,
        visitorName: form.visitorName.trim(),
        visitorEmail: form.visitorEmail.trim(),
        comment: trimmedComment,
        rating: form.rating ? Number(form.rating) : undefined
      }

      const res = await axios.post(
        '/reviews/submit',
        payload,
        customerAuthToken ? { headers: { Authorization: `Bearer ${customerAuthToken}` } } : undefined
      )
      toast.success(res.data.message || 'Review submitted — awaiting approval')
      setForm({ visitorName: '', visitorEmail: '', comment: '', rating: '' })

      // Call the callback if provided
      if (onReviewSubmit) {
        onReviewSubmit()
      }
    } catch (err) {
      console.error('Error submitting review:', err)
      toast.error(err.response?.data?.message || 'Failed to submit review')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div 
      className="rounded-lg shadow-lg p-8 max-w-xl mx-auto transition-colors duration-200" 
      style={isFormHovered && hoverFormStyle ? hoverFormStyle : formStyle}
      onMouseEnter={() => setIsFormHovered(true)}
      onMouseLeave={() => setIsFormHovered(false)}
    >
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input name="visitorName" value={form.visitorName} onChange={handleChange} placeholder="Your name*" className="input-field w-full" style={inputStyle} />
          <input name="visitorEmail" value={form.visitorEmail} onChange={handleChange} placeholder="Email (optional)" className="input-field w-full" style={inputStyle} />
        </div>
        <textarea name="comment" value={form.comment} onChange={handleChange} placeholder="Write your review*" rows={4} className="input-field w-full" style={inputStyle} />
        <div className="flex items-center gap-3">
          <select name="rating" value={form.rating} onChange={handleChange} className="input-field w-32" style={inputStyle}>
            <option value="">Rating (optional)</option>
            <option value="5">5 - Excellent</option>
            <option value="4">4 - Very good</option>
            <option value="3">3 - Good</option>
            <option value="2">2 - Fair</option>
            <option value="1">1 - Poor</option>
          </select>
          <button 
            type="submit" 
            disabled={loading} 
            className="py-2 px-4 text-white rounded transition-colors duration-200" 
            style={isButtonHovered && hoverButtonStyle ? hoverButtonStyle : buttonStyle}
            onMouseEnter={() => setIsButtonHovered(true)}
            onMouseLeave={() => setIsButtonHovered(false)}
          >
            {loading ? 'Sending...' : 'Submit Review'}
          </button>
        </div>
      </form>
    </div>
  )
}
